import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { LoadingPage } from '@/components/common/Loading';
import { Users, ChevronRight, Crown, Star } from 'lucide-react';
import { translateCountryName } from '@/lib/wcTranslations';

const API_BASE = '';

const TEAM_COLORS = {
  'USA': 'from-blue-900 to-red-900', 'Canada': 'from-red-900 to-red-800', 'Mexico': 'from-green-900 to-red-900',
  'Brazil': 'from-yellow-900 to-green-900', 'Argentina': 'from-sky-900 to-sky-800', 'France': 'from-blue-900 to-blue-800',
  'England': 'from-red-900 to-white/10', 'Germany': 'from-slate-800 to-yellow-900', 'Spain': 'from-red-900 to-yellow-900',
  'Türkiye': 'from-red-900 to-red-800', 'Portugal': 'from-green-900 to-red-900', 'Netherlands': 'from-orange-900 to-orange-800',
};

function _teamSlug(name) {
  const map = { 'Türkiye': 'turkiye', 'USA': 'usa', 'Canada': 'canada', 'Mexico': 'mexico', 'Brazil': 'brazil', 'France': 'france', 'Argentina': 'argentina', 'England': 'england', 'Germany': 'germany', 'Spain': 'spain', 'Portugal': 'portugal', 'Netherlands': 'netherlands', 'Croatia': 'croatia', 'Belgium': 'belgium', 'Japan': 'japan', 'South Korea': 'south-korea', 'Australia': 'australia', 'Morocco': 'morocco', 'Senegal': 'senegal', 'Uruguay': 'uruguay', 'Colombia': 'colombia', 'Ecuador': 'ecuador', 'Paraguay': 'paraguay', 'Switzerland': 'switzerland', 'Austria': 'austria', 'Sweden': 'sweden', 'Norway': 'norway', 'Scotland': 'scotland', 'Iran': 'iran', 'Saudi Arabia': 'saudi-arabia', 'Qatar': 'qatar', 'Egypt': 'egypt', 'Tunisia': 'tunisia', 'Algeria': 'algeria', 'Ivory Coast': 'ivory-coast', 'Ghana': 'ghana', 'Congo DR': 'congo-dr', 'South Africa': 'south-africa', 'Cape Verde Islands': 'cape-verde', 'New Zealand': 'new-zealand', 'Panama': 'panama', 'Haiti': 'haiti', 'Bosnia & Herzegovina': 'bosnia', 'Czech Republic': 'czech-republic', 'Jordan': 'jordan', 'Iraq': 'iraq', 'Uzbekistan': 'uzbekistan', 'Curaçao': 'curacao' };
  return map[name] || name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

export default function WorldCupTeamsPage() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;

  useEffect(() => {
    fetch(`${API_BASE}/api/worldcup/teams-all?lang=${currentLang}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [currentLang]);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const teamPath = currentLang === 'tr' ? 'takim' : 'team';

  return (
    <div data-testid="wc-teams-page">
      <WCSubmenu />
      <SEOHead title={t('wc.teamsLabel') + ' - ' + t('wc.breadcrumb') + ' | ODDSIS'}
        canonical={`https://oddsis.com/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/takimlar' : 'world-cup-2026/teams'}`} />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0a1628 40%, #1a2744 100%)' }}>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-8">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Users className="w-8 h-8 inline mr-3 text-amber-400" />
            {t('wc.teamsLabel').toUpperCase()}
          </h1>
          <p className="text-sm text-slate-400">{data.total} {t('wc.participatingTeams')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-8">
        {/* Host Countries - Large Cards */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-5 h-5 text-amber-400" />
            <h2 className="text-xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('wc.hostCountries')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.host_teams?.map(team => (
              <Link key={team.team_id} to={`${wcBase}/${teamPath}/${_teamSlug(team.name)}`} data-testid={`host-team-${_teamSlug(team.name)}`}
                className={`group relative overflow-hidden rounded-2xl border border-slate-800 hover:border-amber-500/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(245,158,11,0.15)]`}>
                <div className={`bg-gradient-to-br ${TEAM_COLORS[team.name] || 'from-slate-800 to-slate-900'} p-6 pb-8`}>
                  <div className="flex items-start justify-between">
                    <img src={team.logo} alt={team.name} className="w-16 h-16 drop-shadow-xl" />
                    <span className="text-[10px] bg-white/10 backdrop-blur-xl text-white/80 px-2.5 py-1 rounded-full font-bold uppercase tracking-wider">{t('wc.hostLabel')}</span>
                  </div>
                  <h3 className="text-2xl font-black text-white mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{translateCountryName(team.name, currentLang)}</h3>
                </div>
                <div className="bg-slate-900 px-6 py-3 flex items-center justify-between border-t border-slate-800">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-500">{t('wc.groupLabel')}</span>
                    <span className="text-xs text-amber-400 font-bold">{team.group}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-amber-400 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Qualified Teams - Grid */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-5 h-5 text-amber-400" />
            <h2 className="text-xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('wc.qualifiedTeams')}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {data.qualified_teams?.map(team => {
              const isTR = team.name === 'Türkiye';
              return (
                <Link key={team.team_id} to={`${wcBase}/${teamPath}/${_teamSlug(team.name)}`} data-testid={`team-${_teamSlug(team.name)}`}
                  className={`group bg-slate-900/50 backdrop-blur-md border rounded-xl p-4 transition-all duration-300 hover:border-amber-500/50 hover:bg-slate-800/80 hover:-translate-y-1 ${
                    isTR ? 'border-red-500/30 bg-red-950/20 ring-1 ring-red-500/10' : 'border-slate-800'
                  }`}>
                  <img src={team.logo} alt={team.name} className="w-12 h-12 mx-auto mb-2 drop-shadow-lg group-hover:scale-110 transition-transform" />
                  <p className={`text-xs font-bold text-center truncate ${isTR ? 'text-red-400' : 'text-white'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{translateCountryName(team.name, currentLang)}</p>
                  <p className="text-[10px] text-center text-amber-400/60 mt-0.5">{team.group}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
